<template>
  <BaseLoading
    v-if="isGripAnalysisStrategiesLoading"
    class="background-white"
  />
  <div v-else :class="['flex flex-column top', $style.strategyContainer]">
    <b-collapse
      :open="true"
      aria-id="contentIdForStrategies"
      class="width-100"
      :class="{ [$style.itemListContainer]: itemList.length > 0 }"
    >
      <template #trigger="props">
        <div
          v-if="itemList.length > 0 || showStrategyDraggableBox"
          :class="[
            'grip-collapse-container-btn',
            !props.open ? 'border-bottom-Gray86' : null,
          ]"
        >
          <b-icon
            :icon="!props.open ? 'chevron-down' : 'chevron-up'"
            size="is-medium"
          >
          </b-icon>
          <a :class="$style.triggerBtn" aria-controls="contentIdForStrategies">
            Strategy(ies)
          </a>
        </div>
      </template>
      <div
        v-for="(item, index) in itemList"
        :key="index"
        :id="'analysis_strategy_' + item.id"
        :class="[$style.itemContainer, 'has-position-relative']"
      >
        <div :class="['flex flex-row top', $style.itemBox]">
          <div>
            <b :class="$style.title">
              {{ `${index + 1}. ` }}
            </b>
          </div>
          <div class="flex cell flex-column">
            <b :class="$style.title">
              {{ item.title }}
            </b>
            <div class="flex flex-row top">
              <p
                v-if="item.gripDomainCategory"
                :class="['margin-right8', $style.categoryBox]"
              >
                {{ getUpperCase(item.gripDomainCategory.name) }}
              </p>
            </div>
            <p :class="$style.description">
              {{ item.description }}
            </p>
          </div>
          <div v-if="!isDataCollector">
            <a @click="handleEdit(item)" :class="$style.editBtn">
              <b-icon icon="pencil" size="is-small" />
            </a>
            <a @click="handleDelete(item.id)" :class="$style.deletebtn">
              <b-icon icon="delete" size="is-small" />
            </a>
          </div>
        </div>
        <!-- Conditionally render overlay if this strategy is being edited and modal is minimized -->
        <div
          v-if="item.id === editingStrategyId && isMinimized"
          class="shared-list-item-edit-overlay"
          @click="restoreStrategyModal"
        >
          <span class="shared-list-item-overlay-text">
            Resume Edit <i class="mdi mdi-arrow-expand"></i>
          </span>
        </div>
      </div>
      <div
        v-if="showStrategyDraggableBox && !isDataCollector"
        :class="[
          'flex flex-column top',
          $style.draggableContainer,
          'has-position-relative',
        ]"
      >
        <div
          id="strategyDropZone"
          @dragover="onDragOver($event)"
          @drop="onDrop($event, 'strategyDropZone')"
          :class="['flex flex-column top', $style.labelBox]"
        >
          <p :class="$style.dragDropContainerText">
            Drag and drop existing Strategy
          </p>
          <p :class="$style.dragDropContainerSecondText">
            or
            <a @click="openStrategyModal()" :class="$style.addNewItem"
              >create new</a
            >
          </p>
        </div>

        <div
          v-if="isModalActive && isMinimized && isNew"
          class="shared-list-item-edit-overlay"
          @click="restoreStrategyModal"
        >
          <span class="shared-list-item-overlay-text">
            Resume Create <i class="mdi mdi-arrow-expand"></i>
          </span>
        </div>
      </div>
      <div v-else-if="!isDataCollector" class="padding-16">
        <div :class="$style.addNewItem">
          <a @click="handleShowDraggableBox()">+ Add Strategy</a>
        </div>
      </div>
    </b-collapse>
    <StrategyModal
      v-if="isModalActive"
      :key="modalKey"
      :isModalActive="isModalActive"
      :projectId="projectId"
      :isNew="isNew"
      :draggable="true"
      :gripStrategyDetails="gripStrategyDetailsForEdit"
      :isMinimized="isMinimized"
      @close="closeStrategyModal"
      @create="handleStrategyCreate"
      @update="handleStrategyUpdate"
      @minimize="minimizeStrategyModal"
      @restore="restoreStrategyModal"
    />
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  CreateGripAnalysisStrategyPayload,
  CreateGripStrategyBase,
  GripAnalysisStrategy,
  GripItemReloadDTO,
  GripItemType,
  GripStrategyDTO,
  GripStrategyStatus,
  UpdateGripStrategyBase,
} from '../../../store/types/grip.types';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import StrategyModal from '../Strategy/StrategyModal.vue';
import DeleteModalBox from '../../../components/DeleteModalBox.vue';
import { namespace } from 'vuex-class';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    BaseLoading,
    StrategyModal,
    DeleteModalBox,
    GripAnalysisStrategies,
  },
})
export default class GripAnalysisStrategies extends Vue {
  @Prop() public projectId!: number;
  @Prop() public isDataCollector!: boolean;
  @Prop() public gripAnalysisId!: number;
  @Prop() public gripAnalysisEnablerId!: number;
  @Prop() public gripAnalysisStrategies!: GripAnalysisStrategy[];
  @Prop() public updatedItem!: GripItemReloadDTO;
  public isGripAnalysisStrategiesLoading: boolean = false;
  public showStrategyDraggableBox: boolean = false;
  public itemList: GripAnalysisStrategy[] = [];
  public tempDirtyStrategy: CreateGripStrategyBase = {
    title: '',
    description: '',
    resource: '',
    method: '',
    links: [],
    attachments: [],
    gripDomainCategoryId: null,
  };
  public modalKey: number = Math.floor(Math.random() * 999);
  public isModalActive: boolean = false;
  public isNew: boolean = false;
  public gripStrategyDetailsForEdit: GripStrategyDTO | null = null;
  public isMinimized: boolean = false;

  @unsavedChangesGuard.State('userSelectedKeepEditing')
  public userSelectedKeepEditing!: boolean;

  @unsavedChangesGuard.Action('notifyKeepEditingSelected')
  public notifyKeepEditingSelected!: () => void;

  @unsavedChangesGuard.Action('resetKeepEditingFlag')
  public resetKeepEditingFlag!: () => void;

  get editingStrategyId(): number | null {
    return this.isModalActive && !this.isNew && this.gripStrategyDetailsForEdit
      ? this.gripStrategyDetailsForEdit.id
      : null;
  }

  public getUpperCase(value: string): string {
    return value.toUpperCase();
  }

  public updated(): void {
    if (this.updatedItem.item === GripItemType.AnalysisStrategy) {
      const element = document.getElementById(
        this.updatedItem.item + '_' + this.updatedItem.id,
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else if (this.updatedItem.prevItemId) {
        const prevElement = document.getElementById(
          this.updatedItem.prevItemId as string,
        );
        if (prevElement) {
          prevElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }

    if (this.showStrategyDraggableBox) {
      const element = document.getElementById('strategyDropZone');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  public handleShowDraggableBox(): void {
    this.showStrategyDraggableBox = true;
  }

  public onDragOver(event: any): void {
    const dragSupported = event.dataTransfer.types.length;
    if (dragSupported) {
      event.dataTransfer.dropEffect = 'copy';
      event.preventDefault();
    }
  }

  public onDrop(event: any, id: string): void {
    event.preventDefault();
    const jsonData = event.dataTransfer.getData('application/json');
    const data = JSON.parse(jsonData);
    if (!data) {
      return;
    }
    const { itemType, ...strategyData } = data;
    if (id === 'strategyDropZone' && itemType === 'strategy') {
      const strategyPayload: CreateGripAnalysisStrategyPayload = {
        ...strategyData,
        gripAnalysisId: this.gripAnalysisId,
        gripAnalysisEnablerId: this.gripAnalysisEnablerId,
      };
      this.$emit('addGripAnalysisStrategy', { strategyPayload, isNew: false });
    }
  }

  private _resetLocalModalState(): void {
    this.isModalActive = false;
    this.isMinimized = false;
    this.gripStrategyDetailsForEdit = null;
    this.isNew = false;
    this.modalKey += 1;
  }

  public openStrategyModal(): void {
    this._resetLocalModalState();
    this.isNew = true;
    this.isModalActive = true;
  }

  public handleEdit(strategy: GripAnalysisStrategy): void {
    const { status, createdDate, updatedDate, ...strategyDetails } = strategy;

    this._resetLocalModalState();
    this.gripStrategyDetailsForEdit = strategyDetails as GripStrategyDTO;
    this.isNew = false;
    this.isModalActive = true;
  }

  public async closeStrategyModal(): Promise<void> {
    this._resetLocalModalState();
  }

  public minimizeStrategyModal(): void {
    this.isMinimized = true;
  }

  public restoreStrategyModal(): void {
    this.isMinimized = false;
  }

  public handleStrategyCreate(gripStrategy: CreateGripStrategyBase): void {
    const strategyPayload: CreateGripAnalysisStrategyPayload = {
      ...gripStrategy,
      gripAnalysisId: this.gripAnalysisId,
      gripAnalysisEnablerId: this.gripAnalysisEnablerId,
    };
    this.$emit('addGripAnalysisStrategy', {
      strategyPayload,
      isNew: true,
    });

    this._resetLocalModalState();

    this.showStrategyDraggableBox = false;
  }

  public handleStrategyUpdate(
    gripStrategyUpdateData: UpdateGripStrategyBase & { id: number },
  ): void {
    const { id, ...changesToApply } = gripStrategyUpdateData;
    let actualChangesExist = Object.keys(changesToApply).length > 0;

    if (this.gripStrategyDetailsForEdit) {
      let hasMeaningfulChange = false;
      for (const key in changesToApply) {
        if (changesToApply.hasOwnProperty(key)) {
          if (
            JSON.stringify((this.gripStrategyDetailsForEdit as any)[key]) !==
            JSON.stringify((changesToApply as any)[key])
          ) {
            hasMeaningfulChange = true;
            break;
          }
        }
      }
      actualChangesExist = hasMeaningfulChange;
    }

    if (actualChangesExist) {
      this.$emit('updateGripAnalysisStrategy', {
        ...gripStrategyUpdateData, // Send all data including ID
        gripAnalysisId: this.gripAnalysisId,
        gripAnalysisEnablerId: this.gripAnalysisEnablerId,
      });
      this.$emit('handleUpdatedItemReload', {
        item: GripItemType.AnalysisStrategy,
        id: gripStrategyUpdateData.id,
        prevItemId: this.getPrevItemId(gripStrategyUpdateData.id),
      });
    }
    // Always close modal and notify, regardless of whether an API call was made.
    this._resetLocalModalState();
  }

  public handleDelete(gripAnalysisStrategyId: number): void {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Strategy?',
        modalContent:
          'Are you sure you want to delete this strategy? This action cannot be undone.',
      },
      events: {
        close: () => {
          this.$emit('close');
        },
        confirm: () => {
          this.$emit('deleteGripAnalysisStrategy', gripAnalysisStrategyId);
          this.$emit('handleUpdatedItemReload', {
            item: GripItemType.AnalysisStrategy,
            id: gripAnalysisStrategyId,
            prevItemId: this.getPrevItemId(gripAnalysisStrategyId),
          });
        },
      },
    });
  }

  public getPrevItemId(gripStrategyId: number): string {
    const strategyIndex: number = this.itemList.findIndex(
      (item) => item.id === gripStrategyId,
    );

    return strategyIndex > 0
      ? GripItemType.AnalysisStrategy +
          '_' +
          this.itemList[strategyIndex - 1].id
      : GripItemType.AnalysisEnabler + '_' + this.gripAnalysisEnablerId;
  }

  @Watch('userSelectedKeepEditing')
  public onUserSelectedKeepEditing(newValue: boolean): void {
    this.resetKeepEditingFlag();
    if (newValue && this.isMinimized && this.isModalActive) {
      this.restoreStrategyModal();
    }
  }

  @Watch('gripAnalysisStrategies', { deep: true, immediate: true })
  public onGripAnalysisStrategiesChanged(
    newStrategies: GripAnalysisStrategy[] | undefined,
  ): void {
    if (newStrategies) {
      // Strategies are already filtered for ACTIVE status by GripAnalysisPane.
      this.itemList = [...newStrategies].sort((a, b) => a.id - b.id);
    } else {
      this.itemList = [];
    }
    // Note: showStrategyDraggableBox is not updated here.
  }
}
</script>
<style lang="scss" module>
.strategyContainer {
  width: 100%;
  padding-left: 40px;
}

.triggerBtn {
  background: none;
  border: none;
  padding: 10px 0px 0px 0px;
  margin-left: unset;
  font-weight: 400;
  font-size: 14px;
  color: #444f51;

  &:focus {
    box-shadow: none;
    border: none;
  }

  &:hover,
  &:active {
    border: none;
  }
}

.draggableContainer {
  justify-content: center;
  align-items: center;
  padding: 36px 20px;
  background: #fbfbfb;
  border: 1px dashed #9f9f9f;
  border-radius: 6px;
  box-sizing: border-box;
  margin: 0.5rem 0;

  .labelBox {
    align-items: center;

    .dragDropContainerText {
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      color: #9f9f9f;
      margin-bottom: 0.5rem;
    }

    .dragDropContainerSecondText {
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      color: #444f51;
    }
  }
}

.itemListContainer {
  .collapse-content {
    border: 1px solid #e6ecf6;
    border-radius: 4px;
    padding: 0.5rem;
  }
}

.itemContainer {
  margin-bottom: 1rem;
}

.itemContainer:last-child {
  margin-bottom: 0;
}

.itemBox {
  padding: 16px;
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-sizing: border-box;

  .title {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: #444f51;
    margin-bottom: 0.5rem;
    margin-right: 0.5rem;
    max-width: 70ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .categoryBox {
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    background: #e6ecf6;
    border-radius: 4px;
    margin-bottom: 0.5rem;
    color: #444f51;
    width: fit-content;
    padding: 2px 4px;
  }

  .description {
    max-width: 70ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 0.5rem;
  }

  .editBtn {
    color: #21b5ea;
    padding-right: 12px;
  }
  .deletebtn {
    color: #21b5ea;
  }
}

.addNewItem {
  color: #00205b;
  font-weight: bold;
  font-size: 14px;
}
</style>
